// components/common/AlertesModal.js
// Sauvegarde de critères de recherche en localStorage + affichage des alertes
import React, { useState, useEffect } from 'react';
import './AlertesModal.css';

const STORAGE_KEY = 'darna-immo_alertes';

function loadAlertes() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
}

function saveAlertes(arr) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); } catch {}
}

const TYPE_OPTIONS = ['', 'Appartement', 'Villa', 'Terrain', 'Duplex', 'Studio', 'Local commercial'];

export function AlertesModal({ open, onClose, defaultFiltres = {} }) {
  const [alertes, setAlertes] = useState(loadAlertes);
  const [form, setForm] = useState({
    nom: '',
    wilaya: defaultFiltres.quartier || '',
    type: defaultFiltres.typeLogement || '',
    prixMin: defaultFiltres.prixMin || '',
    prixMax: defaultFiltres.prixMax || '',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (open) {
      setAlertes(loadAlertes());
      setSaved(false);
      setForm({
        nom: '',
        wilaya: defaultFiltres.quartier || '',
        type: defaultFiltres.typeLogement || '',
        prixMin: defaultFiltres.prixMin || '',
        prixMax: defaultFiltres.prixMax || '',
      });
    }
  }, [open, defaultFiltres.quartier, defaultFiltres.typeLogement, defaultFiltres.prixMin, defaultFiltres.prixMax]);

  if (!open) return null;

  const handleAdd = (e) => {
    e.preventDefault();
    if (!form.nom.trim()) return;
    const nouvelleAlerte = {
      id: Date.now(),
      nom: form.nom.trim(),
      wilaya: form.wilaya,
      type: form.type,
      prixMin: form.prixMin,
      prixMax: form.prixMax,
      createdAt: new Date().toISOString(),
    };
    const updated = [...alertes, nouvelleAlerte];
    setAlertes(updated);
    saveAlertes(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    setForm({ nom: '', wilaya: '', type: '', prixMin: '', prixMax: '' });
  };

  const handleDelete = (id) => {
    const updated = alertes.filter(a => a.id !== id);
    setAlertes(updated);
    saveAlertes(updated);
  };

  return (
    <div className="alertes-overlay" onClick={onClose}>
      <div className="alertes-panel" onClick={e => e.stopPropagation()}>
        <div className="alertes-header">
          <div className="alertes-header-left">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
            <h3>Alertes immobilières</h3>
          </div>
          <button className="alertes-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="alertes-body">
          <p className="alertes-desc">
            Sauvegardez vos critères de recherche. Vous serez notifié à chaque visite si de nouvelles annonces correspondent.
          </p>

          {/* Formulaire */}
          <form className="alertes-form" onSubmit={handleAdd}>
            <div className="alertes-form-row">
              <div className="alertes-form-group">
                <label>Nom de l'alerte</label>
                <input
                  type="text"
                  placeholder="Ex: Appartement Hydra budget 60M"
                  value={form.nom}
                  onChange={e => setForm({...form, nom: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="alertes-form-grid">
              <div className="alertes-form-group">
                <label>Wilaya / Zone</label>
                <input
                  type="text"
                  placeholder="Ex: Alger, Oran..."
                  value={form.wilaya}
                  onChange={e => setForm({...form, wilaya: e.target.value})}
                />
              </div>
              <div className="alertes-form-group">
                <label>Type</label>
                <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                  {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t || 'Tous'}</option>)}
                </select>
              </div>
              <div className="alertes-form-group">
                <label>Prix min (DZD)</label>
                <input
                  type="number"
                  placeholder="0"
                  value={form.prixMin}
                  onChange={e => setForm({...form, prixMin: e.target.value})}
                />
              </div>
              <div className="alertes-form-group">
                <label>Prix max (DZD)</label>
                <input
                  type="number"
                  placeholder="99 999 999"
                  value={form.prixMax}
                  onChange={e => setForm({...form, prixMax: e.target.value})}
                />
              </div>
            </div>
            <button type="submit" className="alertes-btn-add">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Créer l'alerte
            </button>
            {saved && (
              <div className="alertes-saved-msg">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Alerte sauvegardée !
              </div>
            )}
          </form>

          {/* Liste des alertes */}
          {alertes.length > 0 && (
            <div className="alertes-list">
              <h4>Vos alertes actives ({alertes.length})</h4>
              {alertes.map(a => (
                <div key={a.id} className="alerte-item">
                  <div className="alerte-item-info">
                    <div className="alerte-item-name">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                        <path d="M13.73 21a2 2 0 01-3.46 0"/>
                      </svg>
                      {a.nom}
                    </div>
                    <div className="alerte-item-tags">
                      {a.wilaya && <span className="alerte-tag">{a.wilaya}</span>}
                      {a.type && <span className="alerte-tag">{a.type}</span>}
                      {a.prixMin && <span className="alerte-tag">min {Number(a.prixMin).toLocaleString('fr-DZ')} DZD</span>}
                      {a.prixMax && <span className="alerte-tag">max {Number(a.prixMax).toLocaleString('fr-DZ')} DZD</span>}
                      {!a.wilaya && !a.type && !a.prixMin && !a.prixMax && <span className="alerte-tag alerte-tag-all">Tous biens</span>}
                    </div>
                  </div>
                  <button className="alerte-delete-btn" onClick={() => handleDelete(a.id)} title="Supprimer">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {alertes.length === 0 && (
            <p className="alertes-empty">Aucune alerte active. Créez-en une ci-dessus.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Icône avec badge dans la navbar
export function AlertesBadge({ onClick }) {
  const count = loadAlertes().length;

  return (
    <button className="alertes-badge-btn" onClick={onClick} title="Mes alertes">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 01-3.46 0"/>
      </svg>
      {count > 0 && <span className="alertes-count">{count}</span>}
    </button>
  );
}
